<template>
  <div>
    <v-row>
      <!-- Per-Job Status Cards -->
      <v-col cols="12" v-if="jobKeys.length">
        <v-row>
          <v-col cols="12" md="6" v-for="jobId in jobKeys" :key="jobId">
            <v-card flat class="pa-3 mb-2" :outlined="true">
              <v-card-title class="pa-0 subtitle-2 font-weight-bold mb-2 d-flex align-center">
                {{ jobs[jobId]?.label || jobId }}
                <v-chip
                  v-if="jobs[jobId]?.enabled === false"
                  x-small
                  color="grey darken-1"
                  text-color="white"
                  class="ml-2"
                >DISABLED</v-chip>
              </v-card-title>
              <v-row dense align="center">
                <v-col cols="4">
                  <div class="caption grey--text">Day</div>
                  <div class="body-2">{{ jobs[jobId]?.day === 'last' ? 'Last day of month' : 'Day ' + jobs[jobId]?.day }}</div>
                </v-col>
                <v-col cols="4">
                  <div class="caption grey--text">Time</div>
                  <div class="body-2">{{ jobs[jobId]?.time || '23:59' }}</div>
                </v-col>
                <v-col cols="4" class="text-right">
                  <v-btn x-small color="primary" @click="openJobDialog(jobId)">Edit</v-btn>
                </v-col>
              </v-row>
              <v-row dense class="mt-1">
                <v-col cols="6">
                  <div class="caption grey--text">Last Run</div>
                  <div class="caption font-weight-medium">{{ status.jobs?.[jobId]?.lastRun || '—' }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="caption grey--text">Next Run</div>
                  <div class="caption font-weight-medium">{{ status.jobs?.[jobId]?.nextRun || '—' }}</div>
                </v-col>
              </v-row>
              <v-row dense class="mt-1" v-if="status.jobs?.[jobId]?.nextRunInSeconds !== undefined">
                <v-col cols="12">
                  <div class="caption grey--text">Countdown</div>
                  <div class="caption font-weight-medium">{{ jobCountdowns[jobId] || '—' }}</div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-btn small color="primary" @click="refreshAll" :loading="statusLoading" class="mb-3">
          <v-icon small left>mdi-refresh</v-icon> Refresh
        </v-btn>
        <v-btn small color="warning" class="mb-3 ml-1" @click="resetScheduler" :loading="resetting">
          <v-icon small left>mdi-restart</v-icon> Reset Cron Status
        </v-btn>
        <v-btn small color="info" class="mb-3 ml-1" @click="recoverClaims" :loading="recovering">
          <v-icon small left>mdi-link-variant-remove</v-icon> Recover Orphaned Claims
        </v-btn>
        <v-chip v-if="status.lastError" small color="error" text-color="white" class="ml-2">Error: {{ status.lastError }}</v-chip>
      </v-col>

      <!-- Edit Job Dialog -->
      <v-dialog v-model="editDialog" width="400" persistent>
        <v-card v-if="editingJob">
          <v-card-title class="subtitle-1 font-weight-bold">Edit {{ editingJob.label }}</v-card-title>
          <v-card-text>
            <v-select v-model="editingJob.day" :items="dayOptions" label="Day of Month" dense class="pb-3" />
            <v-dialog v-model="timeDialog" width="290" persistent>
              <template v-slot:activator="{ on }">
                <v-text-field :value="editingJob.time" label="Time (24hr)" dense readonly v-on="on" class="pb-3" />
              </template>
              <v-card>
                <v-time-picker v-model="editingJob.time" format="24hr" @click:minute="timeDialog = false" />
              </v-card>
            </v-dialog>
            <v-switch v-model="editingJob.enabled" label="Enabled" dense class="mt-2" color="primary" />
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="editDialog = false">Cancel</v-btn>
            <v-spacer />
            <v-btn color="primary" @click="saveJobSettings">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Workflow Migrations (active month processing) -->
      <v-col cols="12">
        <v-card flat>
          <v-card-title class="pa-0 subtitle-1 font-weight-bold mb-3">
            <v-icon small class="mr-1">mdi-play-circle</v-icon> Workflow Migrations
            <span class="caption grey--text ml-2">(active month only — mirrors cron jobs)</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-row>
              <v-col cols="6" sm="4" md="2" v-for="migration in workflowMigrations" :key="migration.action">
                <v-card outlined class="pa-2 mb-2 text-center">
                  <div class="text-h6 font-weight-bold">{{ migration.count }}</div>
                  <div class="caption grey--text">{{ migration.label }}</div>
                  <v-btn
                    v-if="!migration.noButton"
                    x-small color="primary" class="mt-1"
                    :loading="running === migration.action"
                    :disabled="migration.count === 0"
                    @click="runMigration(migration.action)"
                  >
                    Run Now
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Emergency Triggers (backup for special situations) -->
      <v-col cols="12">
        <v-card flat>
          <v-card-title class="pa-0 subtitle-1 font-weight-bold mb-3 mt-2">
            <v-icon small class="mr-1">mdi-alert-circle-outline</v-icon> Emergency Triggers
            <span class="caption grey--text ml-2">(backup — use sparingly)</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-row>
              <v-col cols="6" sm="4" md="2" v-for="migration in emergencyMigrations" :key="migration.action">
                <v-card outlined class="pa-2 mb-2 text-center">
                  <div class="text-h6 font-weight-bold">{{ migration.count }}</div>
                  <div class="caption grey--text">{{ migration.label }}</div>
                  <v-btn
                    x-small color="warning" class="mt-1"
                    :loading="running === migration.action"
                    :disabled="migration.count === 0"
                    @click="runMigration(migration.action)"
                  >
                    Run Now
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import api from "@/services/api";
import ClaimMonthlyDataService from "@/services/ClaimMonthlyDataService";

export default {
  name: "ManagementClaimSettings",
  data() {
    return {
      statusLoading: false,
      resetting: false,
      recovering: false,
      running: null,
      editDialog: false,
      timeDialog: false,
      editingJob: null,
      status: { jobs: {}, lastError: null },
      jobs: {},
      jobKeys: [],
      jobCountdowns: {},
      countdownTimer: null,
      dayOptions: [
        { text: 'Last day of month', value: 'last' },
        ...Array.from({ length: 31 }, (_, i) => ({ text: `Day ${i + 1}`, value: i + 1 })),
      ],
      workflowMigrations: [
        { action: "create-groups", label: "Create Groups from Claims", count: 0 },
        { action: "adjuster-lock", label: "DRAFT → MANAGER_REVIEW", count: 0 },
        { action: "manager-approve", label: "MANAGER_REVIEW → MGR_APPROVED", count: 0 },
        { action: "manager-to-clerk-review", label: "MGR_APPROVED → CLERK_REVIEW", count: 0 },
        { action: "clerk-review", label: "CLERK_REVIEW → CLERK_APPROVED", count: 0 },
        { action: "clerk-forward", label: "CLERK_APPROVED → PAYROLL_MGR", count: 0 },
        { action: "payroll-approve", label: "PAYROLL_MGR → PAID", count: 0 },
        { action: "paid-stats", label: "✅ PAID (completed)", count: 0, noButton: true },
      ],
      emergencyMigrations: [
        { action: "activate", label: "PRE-DRAFT → DRAFT", count: 0 },
        { action: "deduplicate-claims", label: "Deduplicate Claims in Groups", count: 0 },
        { action: "carry-forward", label: "Unpaid → PENDING", count: 0 },
      ],
    };
  },
  async mounted() {
    await this.loadSettings();
    await this.loadCounts();
    await this.loadStatus();
    this.countdownTimer = setInterval(() => this.tickCountdown(), 1000);
  },
  beforeDestroy() {
    clearInterval(this.countdownTimer);
  },
  methods: {
    tickCountdown() {
      if (!this.status.jobs) return;
      Object.keys(this.status.jobs).forEach(jobId => {
        const job = this.status.jobs[jobId];
        if (job.nextRunInSeconds !== undefined) {
          job.nextRunInSeconds--;
          if (job.nextRunInSeconds <= 0) { this.loadStatus(); return; }
          const h = Math.floor(job.nextRunInSeconds / 3600);
          const m = Math.floor((job.nextRunInSeconds % 3600) / 60);
          const s = job.nextRunInSeconds % 60;
          this.jobCountdowns[jobId] = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }
      });
    },
    async refreshAll() {
      await Promise.all([this.loadCounts(), this.loadStatus()]);
    },
    async resetScheduler() {
      if (!confirm('Reset all cron job statuses? Jobs will re-run at their next scheduled time.')) return;
      this.resetting = true;
      try {
        await ClaimMonthlyDataService.resetScheduler();
        this.$store.dispatch("setSnackbar", { showing: true, text: "Cron status reset", color: "warning" });
        await this.loadStatus();
      } catch (e) { this.$store.dispatch("setSnackbar", { showing: true, text: "Reset failed", color: "error" }); }
      finally { this.resetting = false; }
    },
    async recoverClaims() {
      if (!confirm('Reset claimMonthlyId for orphaned claims? Only affects claims whose group was deleted.')) return;
      this.recovering = true;
      try {
        const res = await ClaimMonthlyDataService.recoverClaims();
        this.$store.dispatch("setSnackbar", { showing: true, text: res.data?.message || "Claims recovered", color: "info" });
        await this.loadCounts();
      } catch (e) { this.$store.dispatch("setSnackbar", { showing: true, text: "Recovery failed", color: "error" }); }
      finally { this.recovering = false; }
    },
    async loadStatus() {
      this.statusLoading = true;
      try {
        const res = await ClaimMonthlyDataService.schedulerStatus();
        this.status = res.data || { jobs: {} };
      } catch (e) {
        console.error("Failed to load scheduler status", e.response?.data || e.message);
      } finally {
        this.statusLoading = false;
      }
    },
    async loadSettings() {
      try {
        const res = await api.get("/settings/management");
        const data = res.data || {};
        const claim = data.claimModule || {};
        let rawJobs = claim.jobs || null;

        // Migrate old job names to new names + default enabled=true
        if (rawJobs) {
          const nameMap = { "group-creation": "activate", "manager-forward": "manager-approve", "clerk-forward": "clerk-approve", "clerk-review": "clerk-approve" };
          const migrated = {};
          Object.keys(rawJobs).forEach(oldName => {
            const newName = nameMap[oldName] || oldName;
            migrated[newName] = { ...rawJobs[oldName], enabled: rawJobs[oldName].enabled !== false };
          });
          rawJobs = migrated;
        }

        this.jobs = rawJobs || {
          "activate":        { day: 1,     time: "00:01", label: "Create Groups + Activate (→ DRAFT)", enabled: true },
          "adjuster-lock":   { day: 6,     time: "00:01", label: "Adjuster Lock (DRAFT → MANAGER_REVIEW)", enabled: true },
          "manager-approve": { day: 12,    time: "00:01", label: "Manager Auto-Approve (MGR_REVIEW → MGR_APPROVED)", enabled: true },
          "clerk-approve":   { day: 19,    time: "00:01", label: "Clerk Auto-Approve (→ CLERK_APPROVED)", enabled: true },
          "carry-forward":   { day: "last", time: "23:59", label: "Carry Forward (Unpaid → PENDING)", enabled: true },
        };
        this.jobKeys = Object.keys(this.jobs);
      } catch (e) {
        console.error("Failed to load settings", e);
      }
    },
    async loadCounts() {
      try {
        const res = await ClaimMonthlyDataService.migratePreview();
        const counts = res.data?.counts || {};
        this.workflowMigrations[0].count = counts.CAN_CREATE_GROUPS || 0;     // create-groups
        this.workflowMigrations[1].count = counts.DRAFT || 0;                 // adjuster-lock
        this.workflowMigrations[2].count = counts.MANAGER_REVIEW || 0;        // manager-approve
        this.workflowMigrations[3].count = counts.MANAGER_APPROVED || 0;      // manager-to-clerk-review
        this.workflowMigrations[4].count = counts.PAYROLL_CLERK_REVIEW || 0;  // clerk-review
        this.workflowMigrations[5].count = counts.PAYROLL_CLERK_APPROVED || 0;// clerk-forward
        this.workflowMigrations[6].count = counts.PAYROLL_MANAGER_REVIEW || 0;// payroll-approve
        this.workflowMigrations[7].count = counts.PAID || 0;                 // paid-stats (read-only)
        this.emergencyMigrations[0].count = counts["PRE-DRAFT"] || 0;         // activate
        this.emergencyMigrations[1].count = counts.DUPLICATE_CLAIMS || 0;     // deduplicate-claims
        this.emergencyMigrations[2].count = counts.PENDING || 0;              // carry-forward
      } catch (e) {
        console.error("Failed to load counts", e);
      }
    },
    openJobDialog(jobId) {
      this.editingJob = { id: jobId, ...JSON.parse(JSON.stringify(this.jobs[jobId])) };
      this.editDialog = true;
    },
    async saveJobSettings() {
      if (!this.editingJob) return;
      this.jobs[this.editingJob.id] = {
        day: this.editingJob.day,
        time: this.editingJob.time,
        label: this.editingJob.label,
        enabled: this.editingJob.enabled !== false,
      };
      try {
        const existing = await api.get("/settings/management");
        const allSettings = existing.data || {};
        allSettings.claimModule = allSettings.claimModule || {};
        allSettings.claimModule.jobs = { ...this.jobs };
        await api.put("/settings/management", allSettings);
        this.editDialog = false;
        this.$store.dispatch("setSnackbar", { showing: true, text: "Job settings saved", color: "success" });
        this.$emit("settings-saved", allSettings);
      } catch (e) {
        this.$store.dispatch("setSnackbar", { showing: true, text: e.response?.data?.message || "Failed to save", color: "error" });
      }
    },
    async runMigration(action) {
      this.running = action;
      try {
        const res = await ClaimMonthlyDataService.migrate(action);
        this.$store.dispatch("setSnackbar", { showing: true, text: res.data?.message || "Migration complete", color: "success" });
        await this.loadCounts();
        await this.loadStatus();
      } catch (e) {
        this.$store.dispatch("setSnackbar", { showing: true, text: e.response?.data?.message || "Migration failed", color: "error" });
      } finally {
        this.running = null;
      }
    },
  },
};
</script>
